// Assistant panel state (spec §3.8): open/closed, docked ("pin", ≥1280 px) and the latest `open({ prompt, focus })`
// request, reachable from anywhere inside the shell. Ctrl/⌘+J toggles the panel; closing returns focus to the element
// that opened it. D36: open/collapsed and pinned are remembered per browser (a phone-sized viewport always starts
// collapsed, the panel would cover the page); the panel does not exist on Home (`available` false), where Ctrl/⌘+J
// focuses the Home composer instead.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { AssistantFocus } from "../../api/types-assistant.ts";
import { readLocal, writeLocal } from "../../lib/storage.ts";

export interface AssistantRequest {
  prompt: string | null;
  focus: AssistantFocus;
}

export interface AssistantApi {
  /** Opens the panel; with a `prompt`, asks it right away (with `focus` as the page focus of that question). */
  open: (options?: { prompt?: string; focus?: AssistantFocus }) => void;
  close: () => void;
  toggle: () => void;
  isOpen: boolean;
  pinned: boolean;
  setPinned: (pinned: boolean) => void;
  /** False where the side panel does not exist (Home): the panel and its openers are not rendered. */
  available: boolean;
  /** Bumped by every `open()`: the panel moves focus to its composer only for a fresh open, never for a restored one. */
  openSeq: number;
  /** The pending `open()` request; the panel's conversation takes it once. */
  request: AssistantRequest | null;
  clearRequest: () => void;
}

const AssistantContext = createContext<AssistantApi | null>(null);

const OPEN_KEY = "assistant.open";

function restoredOpen(): boolean {
  if (readLocal<unknown>(OPEN_KEY, false) !== true) return false;
  try {
    return !window.matchMedia("(max-width: 767px)").matches;
  } catch {
    return true;
  }
}

export function AssistantProvider({ children, available = true }: { children: ReactNode; available?: boolean }) {
  const [isOpen, setIsOpen] = useState(restoredOpen);
  const [openSeq, setOpenSeq] = useState(0);
  const [pinned, setPinnedState] = useState(() => readLocal<unknown>("assistant.pinned", false) === true);
  const [request, setRequest] = useState<AssistantRequest | null>(null);
  const openRef = useRef(isOpen);
  const opener = useRef<HTMLElement | null>(null);

  const open = useCallback((options: { prompt?: string; focus?: AssistantFocus } = {}) => {
    if (!openRef.current) opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    openRef.current = true;
    setIsOpen(true);
    setOpenSeq((n) => n + 1);
    writeLocal(OPEN_KEY, true);
    if (options.prompt || options.focus) setRequest({ prompt: options.prompt?.trim() || null, focus: options.focus ?? "general" });
  }, []);

  const close = useCallback(() => {
    if (!openRef.current) return;
    openRef.current = false;
    setIsOpen(false);
    writeLocal(OPEN_KEY, false);
  }, []);

  const toggle = useCallback(() => (openRef.current ? close() : open()), [open, close]);

  const setPinned = useCallback((next: boolean) => {
    setPinnedState(next);
    writeLocal("assistant.pinned", next);
  }, []);

  const clearRequest = useCallback(() => setRequest(null), []);

  // Focus goes back to the opener once the panel is gone.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !isOpen) {
      const el = opener.current;
      if (el && el.isConnected && el !== document.body) el.focus();
    }
    wasOpen.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "j") {
        e.preventDefault();
        if (available) toggle();
        else document.querySelector<HTMLElement>("#main textarea")?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggle, available]);

  const value = useMemo<AssistantApi>(
    () => ({ open, close, toggle, isOpen, pinned, setPinned, available, openSeq, request, clearRequest }),
    [open, close, toggle, isOpen, pinned, setPinned, available, openSeq, request, clearRequest],
  );
  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export function useAssistant(): AssistantApi {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error("useAssistant must be used inside AssistantProvider");
  return ctx;
}
